import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gxMenuApi } from "../../apiConfig/services/customerAbstractApiLayer";

// ==================== TYPES ====================
interface Permission {
  permissionId: number;
  permissionName: string;
}

interface SubMenu {
  menuId: number;
  menuName: string;
  menuUrl?: string | null;
  menuIcon?: string | null;
  enableFlag: "Y" | "N";
  permissions?: Permission[];
}

interface Module {
  moduleId: number;
  moduleName: string;
  moduleUrl?: string | null;
  moduleIcon?: string | null;
  enableFlag: "Y" | "N";
  menus?: SubMenu[];
}

interface ApiResponse {
  status: number;
  errorMessage?: string;
  data: Module | Module[];
}

// ==================== ASYNC THUNK ====================
export const fetchMenuData = createAsyncThunk<
  { raw: Module[]; filtered: Module[] },
  void,
  { rejectValue: string }
>("api/fetchMenuData", async (_, thunkAPI) => {
  try {
    const response = (await gxMenuApi(false)) as ApiResponse;

    if (response.status !== 1) {
      return thunkAPI.rejectWithValue(response.errorMessage || "API Error");
    }

    const rawData: Module[] = Array.isArray(response.data)
      ? response.data
      : [response.data];

    const filteredData: Module[] = rawData
      .filter((item) => item.enableFlag === "Y")
      .map((item) => ({
        moduleId: item.moduleId,
        moduleName: item.moduleName,
        moduleUrl: item.moduleUrl ?? null,
        moduleIcon: item.moduleIcon ?? null,
        enableFlag: "Y",
        menus: (item.menus ?? [])
          .filter((sub) => sub.enableFlag === "Y")
          .map((sub) => ({
            menuId: sub.menuId,
            menuName: sub.menuName,
            menuUrl: sub.menuUrl ?? null,
            menuIcon: sub.menuIcon ?? null,
            enableFlag: "Y",
            permissions: sub.permissions ?? [],
          })),
      }));

    return { raw: rawData, filtered: filteredData };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message || "Unknown Error");
  }
});

// ==================== SLICE ====================
interface ApiState {
  rawData: Module[];
  data: Module[];
  loading: boolean;
  error: string | null;
  menuIds: number[];
  subMenuIds: number[];
}

const initialState: ApiState = {
  rawData: [],
  data: [],
  loading: false,
  error: null,
  menuIds: [],
  subMenuIds: [],
};

const apiSlice = createSlice({
  name: "api",
  initialState,
  reducers: {
    setMenuIds: (state, action) => {
      state.menuIds = action.payload;
    },
    setSubMenuIds: (state, action) => {
      state.subMenuIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuData.fulfilled, (state, action) => {
        state.loading = false;
        state.rawData = action.payload.raw;
        state.data = action.payload.filtered;
        const menuIds = state.data.map((item) => item.moduleId);
        const subMenuIds = state.data.flatMap((menu) =>
          Array.isArray(menu.menus) ? menu.menus.map((sub) => sub.menuId) : [],
        );

        state.menuIds = menuIds;
        state.subMenuIds = subMenuIds;
      })
      .addCase(fetchMenuData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Unknown Error";
      });
  },
});

export const { setMenuIds, setSubMenuIds } = apiSlice.actions;
export default apiSlice.reducer;
